import { createContext, useContext, useEffect, useState } from "react";
import { searchProjects } from "services/supabaseProjects";
import { useAuth } from "database/useAuth";
import { Outlet } from "react-router-dom";

const ProjectSearchContext = createContext();

const ProjectSearchContextProvider = () => {
  const { authContextLoaded, user } = useAuth();
  const [searchTerm, setNewSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [currentFacetSearch, setCurrentFacetSearch] = useState(null);
  const [sortBy, setSort] = useState(
    localStorage.getItem("sort") || "modifiedAt:desc",
  );
  const [searchResults, setSearchResults] = useState({
    facetCounts: [],
    projects: [],
  });

  const setSortBy = (sortBy) => {
    localStorage.setItem("sort", sortBy);
    setSort(sortBy);
  };

  useEffect(() => {
    if (!authContextLoaded || user == null) return;

    const selectedFilters = {};
    Object.entries(filters).forEach(([field, values]) => {
      if (values != null && values.length > 0) {
        selectedFilters[field] = values[0];
      }
    });

    searchProjects(
      user.uid,
      searchTerm && searchTerm.length > 0 ? searchTerm : "",
      {
        distributor: selectedFilters.distributor || null,
        rep: selectedFilters.rep || null,
      },
      sortBy,
      1,
      250,
    ).then((results) => {
      setSearchResults({
        facetCounts: results.facetCounts,
        projects: results.projects,
      });
    }).catch((err) => {
      console.error("Project search failed:", err);
    });
  }, [searchTerm, filters, sortBy, authContextLoaded, user]);

  const toggleFilter = (field, value) => {
    const newFilters = { ...filters };
    if (newFilters[field] == null) {
      newFilters[field] = [value];
    } else {
      if (newFilters[field].includes(value)) {
        newFilters[field] = newFilters[field].filter((v) => v !== value);
      } else {
        newFilters[field].push(value);
      }
    }
    setFilters(newFilters);
  };

  const setSearchTerm = (term) => {
    setNewSearchTerm(term);
    setCurrentFacetSearch(null);
  };

  // If there's a facet search active, filter the facet counts client-side
  const getFilteredFacetCounts = () => {
    if (!searchResults?.facetCounts) return [];
    if (!currentFacetSearch) return searchResults.facetCounts;

    return searchResults.facetCounts.map((facet) => {
      if (currentFacetSearch.field === facet.field_name) {
        return {
          ...facet,
          counts: facet.counts.filter((c) =>
            c.value.toLowerCase().includes(currentFacetSearch.searchTerm.toLowerCase())
          ),
        };
      }
      return facet;
    });
  };

  return (
    <ProjectSearchContext.Provider
      value={{
        searchResults: {
          ...searchResults,
          facetCounts: getFilteredFacetCounts(),
        },
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        filters,
        toggleFilter,
        currentFacetSearch,
        setCurrentFacetSearch,
      }}
    >
      <Outlet />
    </ProjectSearchContext.Provider>
  );
};

export default ProjectSearchContextProvider;

export const useProjectSearch = () => useContext(ProjectSearchContext);
